import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { APP_LABEL, AppName, AppScreen, screenAt } from './appScreens';

/**
 * Choosing where an article's button goes, by screen name.
 *
 * This was a free-text path field, which asked the person writing an article
 * to know that "Security" is `/(app)/security-settings` in one app and
 * `/(auth)/security-settings` in the other — and a typo there does not fail,
 * it just hides the button in the app. So it is a searchable list of the
 * screens the app will actually open, and the route is only ever something
 * picked, never typed.
 *
 * ## What it stores is still the route
 *
 * The article keeps `action.route`, so nothing changes for the API or the
 * apps. A saved route that is not on the list (an older article, or a
 * screen since removed) is shown as such rather than silently cleared: the
 * editor should decide to change it.
 */
export const ScreenPicker: React.FC<{
  label: string;
  value: string;
  screens: AppScreen[];
  onChange: (route: string) => void;
  hint?: string;
}> = ({ label, value, screens, onChange, hint }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = value ? screenAt(value) : undefined;
  const unknown = !!value && !selected;
  const apps = useMemo(
    () => Array.from(new Set(screens.map((s) => s.app))) as AppName[],
    [screens],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return screens;
    return screens.filter((s) =>
      [s.name, s.group, s.route, APP_LABEL[s.app]].some((f) =>
        f.toLowerCase().includes(q),
      ),
    );
  }, [screens, query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    searchRef.current?.focus();
    // Near the foot of a modal the list opens below the fold; bring it up.
    listRef.current?.scrollIntoView?.({ block: 'nearest' });
    const close = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => setActive(0), [query]);

  const pick = (route: string) => {
    onChange(route);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (matches[active]) pick(matches[active].route);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Rendered in order, with a heading whenever the app or group changes.
  let lastHeading = '';

  return (
    <div ref={rootRef}>
      <label
        style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--neutral-700)',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {/* The list hangs off the field itself, so the label above and the
          hint below can be any height without it landing on either. */}
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          style={{
            width: '100%',
            height: 40,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0 12px',
            background: 'var(--neutral-100)',
            border: `1px solid ${unknown ? 'var(--danger-600)' : 'var(--neutral-300)'}`,
            borderRadius: 'var(--radius-md)',
            fontFamily: 'var(--font-family)',
            fontSize: 14,
            textAlign: 'left',
            cursor: 'pointer',
            color: value ? 'var(--neutral-800)' : 'var(--neutral-500)',
          }}
        >
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selected
              ? apps.length > 1
                ? `${selected.name} · ${APP_LABEL[selected.app]}`
                : selected.name
              : unknown
                ? `Not a screen the app opens: ${value}`
                : 'No button'}
          </span>
          <ChevronDown size={16} color="var(--neutral-500)" />
        </button>
        {open ? (
          <div
            ref={listRef}
            style={{
              position: 'absolute',
              zIndex: 20,
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              // `--white` is not a token here, so this was transparent and the
              // form showed through the list.
              background: 'var(--neutral-100)',
              border: '1px solid var(--neutral-300)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-md)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                borderBottom: '1px solid var(--neutral-200)',
              }}
            >
              <Search size={15} color="var(--neutral-500)" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search screens…"
                aria-label="Search screens"
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  fontSize: 14,
                  fontFamily: 'var(--font-family)',
                  background: 'transparent',
                }}
              />
            </div>

            <div role="listbox" aria-label={label} style={{ maxHeight: 320, overflowY: 'auto', padding: 4 }}>
              {!query ? (
                <Option
                  selected={!value}
                  active={false}
                  onPick={() => pick('')}
                  name="No button"
                  meta="The article shows no action"
                  icon={<X size={14} color="var(--neutral-500)" />}
                />
              ) : null}

              {matches.length === 0 ? (
                <p style={{ padding: '12px 10px', margin: 0, fontSize: 13, color: 'var(--neutral-500)' }}>
                  No screen matches “{query.trim()}”.
                </p>
              ) : null}

              {matches.map((screen, index) => {
                const heading =
                  apps.length > 1 ? `${APP_LABEL[screen.app]} · ${screen.group}` : screen.group;
                const showHeading = heading !== lastHeading;
                lastHeading = heading;
                return (
                  <React.Fragment key={screen.route}>
                    {showHeading ? (
                      <div
                        style={{
                          padding: '10px 10px 4px',
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: 0.4,
                          textTransform: 'uppercase',
                          color: 'var(--neutral-500)',
                        }}
                      >
                        {heading}
                      </div>
                    ) : null}
                    <Option
                      selected={screen.route === value}
                      active={index === active}
                      onPick={() => pick(screen.route)}
                      onHover={() => setActive(index)}
                      name={screen.name}
                      meta={screen.route}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
      {unknown || hint ? (
        <span
          style={{
            display: 'block',
            marginTop: 6,
            fontSize: 12,
            color: unknown ? 'var(--danger-600)' : 'var(--neutral-500)',
          }}
        >
          {unknown
            ? 'The app hides this button. Pick a screen from the list, or choose “No button”.'
            : hint}
        </span>
      ) : null}

    </div>
  );
};

const Option: React.FC<{
  name: string;
  meta: string;
  selected: boolean;
  active: boolean;
  onPick: () => void;
  onHover?: () => void;
  icon?: React.ReactNode;
}> = ({ name, meta, selected, active, onPick, onHover, icon }) => (
  <div
    role="option"
    aria-selected={selected}
    onMouseDown={(e) => {
      // Before the search field blurs, so the pick is not lost to it.
      e.preventDefault();
      onPick();
    }}
    onMouseEnter={onHover}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 10px',
      borderRadius: 'var(--radius-sm, 6px)',
      cursor: 'pointer',
      background: active ? 'var(--neutral-100)' : 'transparent',
    }}
  >
    {icon}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 14, fontWeight: selected ? 600 : 400, color: 'var(--neutral-800)' }}>{name}</div>
      <div style={{ fontSize: 11, color: 'var(--neutral-500)', fontFamily: 'var(--font-mono, monospace)' }}>{meta}</div>
    </div>
  </div>
);
